import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Eye, Megaphone, MousePointerClick } from 'lucide-react';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { StatCard } from '@/components/StatCard';
import { CampaignStatusBadge } from '@/components/StatusBadge';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TablePagination } from '@/components/TablePagination';
import { usePagination } from '@/hooks/use-pagination';
import { TableSearch } from '@/components/TableSearch';
import { useTableSearch } from '@/hooks/use-table-search';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queries';
import { daysAgoUtc, formatCount, formatPercent, formatUtcDate, todayUtc } from '@/lib/utils';

export const Route = createFileRoute('/')({ component: Overview });

const WINDOW_DAYS = 30;

function Overview() {
  const from = daysAgoUtc(WINDOW_DAYS - 1);
  const to = todayUtc();

  const campaigns = useQuery({ queryKey: queryKeys.campaigns, queryFn: api.campaigns.list });
  const reporting = useQuery({
    // Deliberately blended. /reporting is where DTC and HCP separate; this page
    // stays the whole-network glance, which is also what keeps the combined
    // number available somewhere once reporting is strictly two tabs.
    queryKey: queryKeys.reporting(from, to, undefined, 'all'),
    queryFn: () => api.reporting.get(from, to),
  });

  const totals = reporting.data?.totals;
  const active = (campaigns.data ?? []).filter((c) => c.status === 'active');
  const needsAttention = (campaigns.data ?? []).filter(
    (c) => c.status !== 'archived' && c.approvedCreativeCount === 0
  );

  const search = useTableSearch(campaigns.data, (c) => [c.name, c.id, c.sponsorName, c.status]);
  const pagination = usePagination(search.filtered);
  return (
    <>
      <PageHeader
        title="Overview"
        description={`Delivery across the network · last ${WINDOW_DAYS} days`}
        actions={
          <Button asChild size="sm">
            <Link to="/campaigns/new">New campaign</Link>
          </Button>
        }
      />
      <PageBody>
        <TableSearch
          search={search}
          placeholder="Search campaigns by name, id, sponsor or status"
          label="Search campaigns"
          noun="campaign"
        />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <StatCard
            label="Active campaigns"
            value={formatCount(active.length)}
            hint={`${campaigns.data?.length ?? 0} total`}
            icon={Megaphone}
            loading={campaigns.isPending}
          />
          <StatCard
            label="Impressions"
            value={formatCount(totals?.impressions ?? 0)}
            icon={Eye}
            loading={reporting.isPending}
          />
          <StatCard
            label="Click-through rate"
            value={formatPercent(reporting.data?.ctr)}
            hint={`${formatCount(totals?.clicks ?? 0)} clicks`}
            icon={MousePointerClick}
            loading={reporting.isPending}
          />
        </div>

        {needsAttention.length > 0 && (
          <Card className="border-amber-300 bg-amber-50 shadow-none dark:border-amber-900 dark:bg-amber-950/40">
            <CardContent className="p-4 text-sm">
              <p className="font-medium text-amber-900 dark:text-amber-200">
                {needsAttention.length} campaign{needsAttention.length === 1 ? '' : 's'} with no
                approved creative
              </p>
              <p className="text-amber-800/80 dark:text-amber-200/70">
                {needsAttention.map((c) => c.name).join(', ')} — these cannot go live until a
                creative is approved.
              </p>
            </CardContent>
          </Card>
        )}

        <Card className="shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Campaigns</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {campaigns.isError ? (
              <ErrorState error={campaigns.error} onRetry={() => void campaigns.refetch()} />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Sponsor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Flight</TableHead>
                    <TableHead className="text-right">Creatives</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.isPending && <LoadingRows columns={5} />}
                  {pagination.pageRows.map((campaign) => (
                    <TableRow key={campaign.id}>
                      <TableCell>
                        <Link
                          to="/campaigns/$campaignId"
                          params={{ campaignId: campaign.id }}
                          className="font-medium hover:underline"
                        >
                          {campaign.name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {campaign.sponsorName}
                      </TableCell>
                      <TableCell>
                        <CampaignStatusBadge status={campaign.status} />
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap text-muted-foreground">
                        {formatUtcDate(campaign.flightStart, 'MMM D')} –{' '}
                        {formatUtcDate(campaign.flightEnd)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {campaign.approvedCreativeCount}/{campaign.creativeCount}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <TablePagination pagination={pagination} label="campaigns" />
            {campaigns.data?.length === 0 && (
              <EmptyState
                title="No campaigns yet"
                description="Create a sponsor, then a campaign, then a creative for it to approve."
                icon={Megaphone}
                action={
                  <Button asChild size="sm">
                    <Link to="/campaigns/new">New campaign</Link>
                  </Button>
                }
              />
            )}
          </CardContent>
        </Card>
      </PageBody>
    </>
  );
}
